import {
  native,
  type Product,
  type SubscriptionOffer,
  type SubscriptionProduct,
} from '@forest/native-services'
import {
  failureCause,
  nativeErrorCause,
  type PaymentPlatform,
  type Server,
  type UserInfo,
} from '@forest/paywall-core'
import type { Loaded } from '~/lib/loaded'
import type { WinBackArm } from '~/lib/win-back-experiment'

type PlanKey = 'annual' | 'monthly'

/**
 * The plans' store SKUs on one platform, shared across clusters, and the
 * resubscribe offer (GLOSSARY.md "Subscription expiration win-back
 * experiment") the promotional annual plan sells the annual SKU with.
 */
export interface PlatformPlans extends Record<PlanKey, string> {
  resubscribeOfferId: string
}

/**
 * The Service API product ids purchase-right needs on one cluster, shared
 * across platforms. The promotional annual plan is the annual SKU recorded
 * under its own id.
 */
export type PlanProductIds = Record<PlanKey | 'promotionalAnnual', number>

/**
 * The resubscribe paywall's plans by platform. `china_android` has none, so
 * the page shows its error dialog there.
 */
export const PLATFORM_PLANS: Partial<Record<PaymentPlatform, PlatformPlans>> = {
  ios: {
    annual: 'forest_annual_early_bird',
    monthly: 'forest_monthly_early_bird_2',
    resubscribeOfferId: 'subscription_expiration_win_back',
  },
  googleplay_android: {
    annual: 'forest_plus-annual-early-bird',
    monthly: 'forest_plus-monthly-early-bird-2',
    resubscribeOfferId: 'subscription-expiration-win-back',
  },
}

/**
 * The resubscribe paywall's product ids by the user's cluster (GLOSSARY.md
 * "Cluster"), not the build mode: one production build serves both `global`
 * and `china`.
 */
export const PLAN_PRODUCT_IDS: Record<Server, PlanProductIds> = {
  dev: { annual: 51, monthly: 57, promotionalAnnual: 279 },
  staging: { annual: 61, monthly: 68, promotionalAnnual: 292 },
  global: { annual: 61, monthly: 64, promotionalAnnual: 378 },
  china: { annual: 61, monthly: 64, promotionalAnnual: 292 },
}

/** The platform's plans; none for an unsupported or unknown platform. */
export function platformPlans(
  platform: PaymentPlatform | null,
): PlatformPlans | undefined {
  return platform ? PLATFORM_PLANS[platform] : undefined
}

/** The user's cluster's product ids; none for a cluster it does not know. */
export function clusterProductIds(
  userInfo: UserInfo,
): PlanProductIds | undefined {
  return Object.hasOwn(PLAN_PRODUCT_IDS, userInfo.server)
    ? PLAN_PRODUCT_IDS[userInfo.server]
    : undefined
}

/**
 * The promotional annual plan's offer (GLOSSARY.md "Promotional offer"),
 * applied at purchase by its id.
 */
export interface Promotion {
  offer: SubscriptionOffer
  /** round(1 − offer price ÷ SKU price), as a whole percentage. */
  discountPercent: number
}

/** The plans the plan picker offers; a plan that does not resolve is null. */
export interface Plans {
  /** The regular annual plan, or the promotional one under `promotion`. */
  annual: Product | null
  monthly: Product | null
  /**
   * Set only when the promotional annual plan replaces the regular one: the
   * displayed arm is then Variant, otherwise Control.
   */
  promotion: Promotion | null
}

function resolvePlan(
  sku: string,
  productId: number,
  storeProducts: SubscriptionProduct[],
): Product | null {
  const storeProduct = storeProducts.find((p) => p.sku === sku)
  if (!storeProduct) return null
  return {
    ...storeProduct,
    product_id: productId,
    receipt_system_product_id: 0,
    product_type: 'subscription',
  }
}

/**
 * The resubscribe offer on the annual SKU's product, found by its id alone:
 * neither its place among the offers nor its period matters. A non-positive
 * store price leaves no discount to compute, so it does not resolve either.
 */
function resolvePromotion(
  annual: Product,
  resubscribeOfferId: string,
): Promotion | null {
  const offer = annual.offers?.find((o) => o.id === resubscribeOfferId)
  if (!offer || !(annual.price_value > 0)) return null
  return {
    offer,
    discountPercent: Math.round(
      (1 - offer.price_value / annual.price_value) * 100,
    ),
  }
}

/**
 * The plans for the arm. Variant shows the promotional annual plan in place
 * of the regular one; when it does not resolve, the Control plans. Control
 * never looks for the resubscribe offer. `null` when no card is left.
 */
export function resolvePlans(
  platformRow: PlatformPlans,
  productIds: PlanProductIds,
  storeProducts: SubscriptionProduct[],
  arm: WinBackArm,
): Plans | null {
  const monthly = resolvePlan(
    platformRow.monthly,
    productIds.monthly,
    storeProducts,
  )
  if (arm === 'Variant') {
    const promotional = resolvePlan(
      platformRow.annual,
      productIds.promotionalAnnual,
      storeProducts,
    )
    const promotion =
      promotional &&
      resolvePromotion(promotional, platformRow.resubscribeOfferId)
    if (promotion) return { annual: promotional, monthly, promotion }
  }
  const annual = resolvePlan(
    platformRow.annual,
    productIds.annual,
    storeProducts,
  )
  return annual || monthly ? { annual, monthly, promotion: null } : null
}

/**
 * Native's subscription products, or why the request failed — no plan
 * resolves from a failure.
 */
export async function loadStoreProducts(): Promise<
  Loaded<SubscriptionProduct[]>
> {
  try {
    const result = await native.iap.getAvailableSubscriptionProducts()
    return result.success
      ? { ok: true, value: result.data.products }
      : { ok: false, cause: nativeErrorCause(result.error) }
  } catch (error) {
    return { ok: false, cause: failureCause(error) }
  }
}
